"use client";

import React from "react";
import { Spinner } from "@heroui/react";
// TODO: Add or contribute type declarations for pptx-preview
// @ts-ignore - pptx-preview doesn't have TypeScript definitions
import { init } from "pptx-preview";

import { findTextMatch, MatchResult } from "@/utils/textMatching";
import { logger } from "@/telemetry";

import {
  buildTextNodeIndex,
  collectTextNodes,
  createHighlightMark,
  findCitationNodeRange,
  scrollToHighlight,
} from "./textNodeHighlight";

export interface PPTXViewerProps {
  /** PPTX document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the PPTX */
  citationText?: string;

  /** Callback when text is found or not found */
  onMatchResult?: (result: MatchResult | null) => void;
}

/**
 * PPTX Viewer component using pptx-preview
 * Renders PPTX and highlights citation text
 *
 * LESSONS LEARNED FROM DOCX:
 * 1. Always render container div (not conditional)
 * 2. Use CSS display:none for loading states
 * 3. Keep direct reference to first highlight mark
 * 4. Use setTimeout for scrolling
 * 5. Character-to-node mapping for precise highlighting
 */
export const PPTXViewer: React.FC<PPTXViewerProps> = ({
  documentData,
  citationText,
  onMatchResult,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [documentText, setDocumentText] = React.useState<string>("");
  const previewerRef = React.useRef<any>(null);

  // Initialize PPTX previewer and render document
  React.useEffect(() => {
    if (!documentData || !containerRef.current) {
      return;
    }

    let isMounted = true;

    const renderPPTX = async () => {
      const container = containerRef.current;

      if (!container) return;

      try {
        setLoading(true);
        setError(null);

        // Clear container
        container.innerHTML = "";

        // Initialize pptx-preview
        if (!previewerRef.current) {
          previewerRef.current = init(container, {
            width: container.clientWidth || 960,
            height: 600,
          });
        }

        // Render PPTX
        await previewerRef.current.preview(documentData);

        if (!isMounted) return;

        // Extract text content from rendered HTML
        const extractedText = container.innerText || container.textContent || "";

        setDocumentText(extractedText);
        setLoading(false);
      } catch (err) {
        logger.error("❌ Failed to render PPTX", err);
        if (isMounted) {
          setError("Failed to render PPTX document");
          setLoading(false);
        }
      }
    };

    renderPPTX();

    return () => {
      isMounted = false;
    };
  }, [documentData]);

  // Find and highlight citation text
  React.useEffect(() => {
    if (!citationText || !documentText || !containerRef.current) return;

    const matchResult = findTextMatch(documentText, citationText);

    if (onMatchResult) {
      onMatchResult(matchResult);
    }

    if (matchResult) {
      highlightTextInDOM(containerRef.current, citationText, matchResult);
    }
  }, [citationText, documentText, onMatchResult]);

  /**
   * Highlight matched text in the rendered PPTX DOM
   * Uses character-to-node mapping for precise highlighting (lesson from DOCX)
   */
  const highlightTextInDOM = (
    container: HTMLElement,
    citationText: string,
    _matchResult: MatchResult,
  ) => {
    try {
      const textNodes = collectTextNodes(container);

      if (textNodes.length === 0) {
        logger.info("⚠️ No text nodes found in PPTX container (slides may still be rendering)");

        return;
      }

      const { startNodeIndex, endNodeIndex } = findCitationNodeRange(
        buildTextNodeIndex(textNodes),
        textNodes.length,
        citationText,
      );
      let firstMark: HTMLElement | undefined;

      // Unlike the DOCX viewer, a failed replacement is not caught per node:
      // it abandons the rest of the highlight through the catch below.
      textNodes.slice(startNodeIndex, endNodeIndex + 1).forEach((node) => {
        const parent = node.parentElement;

        if (!parent) return;

        const mark = createHighlightMark(node.nodeValue || "");

        parent.replaceChild(mark, node);

        // Keep reference to first mark for scrolling (lesson from DOCX)
        firstMark ??= mark;
      });

      scrollToHighlight(firstMark);
    } catch (err) {
      logger.error("Failed to highlight text in PPTX", err);
    }
  };

  return (
    <div className="w-full h-full overflow-auto bg-content1 p-8">
      {/* Always render container so ref is available (lesson from DOCX) */}
      <div
        ref={containerRef}
        className="pptx-container max-w-4xl mx-auto bg-white"
        style={{
          /* PPTX preview styles */
          display: loading || error ? "none" : "block",
        }}
      />

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center h-full">
          <Spinner label="Loading PPTX..." />
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="flex items-center justify-center h-full">
          <p className="text-danger-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PPTXViewer;
